import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { HighlightIndices, Option } from '@rivethealth/data/options';
import Fuse, { FuseResultMatch } from 'fuse.js';
import * as _ from 'lodash';
import { get, isEmpty, sortBy } from 'lodash';
import { ObservableInput } from 'observable-input';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  scan,
  startWith,
  withLatestFrom,
} from 'rxjs/operators';
import { Box } from '../callout/callout-position';
import { EventsService } from '../core/events.service';
import { Loadable, success } from '../helpers/loadable';
import { replayLast } from '../rx/util';
import { MultiSelectNode as Node } from './multi-select-node.component';

/**
 * A multi-select with text search.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'multi-select',
  styleUrls: ['./multi-select.component.scss'],
  templateUrl: './multi-select.component.html',
})
export class MultiSelectComponent {
  constructor(private readonly eventsService: EventsService) {}
  readonly filter = new BehaviorSubject<string>('');
  readonly debouncedFilter = this.filter.pipe(debounceTime(100));

  @Output() readonly filterChange = new EventEmitter<string>();

  readonly select = new BehaviorSubject<[string | null, boolean]>([
    null,
    false,
  ]);

  @ObservableInput()
  @Input('autoFocus')
  private readonly _autoFocus!: Observable<boolean>;
  readonly defaultedAutoFocus = this._autoFocus.pipe(startWith(false));

  @ObservableInput()
  @Input('disabled')
  private readonly _disabled!: Observable<boolean>;
  readonly defaultedDisabled = this._disabled.pipe(startWith(false));

  @ObservableInput()
  @Input('exact')
  private readonly _exact!: Observable<boolean>;
  readonly defaultedExact = this._exact.pipe(startWith(false));

  @ObservableInput()
  @Input('filterable')
  private readonly _filterable!: Observable<boolean>;
  readonly defaultedFilterable: Observable<boolean> = this._filterable.pipe(
    startWith(false),
  );

  @Input()
  maxElements: number;

  @ObservableInput()
  @Input('filterPositionBottom')
  private readonly _filterPositionBottom!: Observable<boolean>;
  readonly defaultedFilterPositionBottom = this._filterPositionBottom.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('options')
  private readonly _options!: Observable<Option[]>;

  @ObservableInput()
  @Input()
  private readonly optionsLoadable!: Observable<Loadable<{
    options: Option[];
    count: number;
  }> | null>;
  readonly defaultedOptionsLoadable = this.optionsLoadable.pipe(
    startWith(null),
  );

  readonly uiOptionsLoadable = this.defaultedOptionsLoadable.pipe(
    map(loadable =>
      loadable ? loadable : { loading: false, erred: false, data: [] },
    ),
  );

  readonly defaultedOptions: Observable<Option[]> = merge(
    this._options,
    this.optionsLoadable.pipe(
      <any>filter(o => !!o),
      success(),
      map((d: any) => d.options),
    ),
  ).pipe(
    startWith([]),
    map(options => options || <Option[]>[]),
    replayLast(),
  );

  @ObservableInput()
  @Input('selectedOptionIds')
  private _selectedOptionIds!: Observable<string[]>;
  readonly defaultedSelectedOptionIds = this._selectedOptionIds.pipe(
    startWith([]),
  );

  @ObservableInput()
  @Input('anchor')
  private readonly _anchor!: Observable<Element | ClientRect | DOMRect>;

  @Input()
  selectedEnabled: boolean = true;
  showSelected: boolean = false;

  @Input()
  displayId: boolean = false;

  private readonly fuzzySearch: Observable<Fuse<Option> | null> = combineLatest(
    [
      this.defaultedOptions,
      this.defaultedOptionsLoadable,
      this.defaultedExact,
      this.defaultedFilterable,
    ],
  ).pipe(
    map(([options, loadable, exact, filterable]) => {
      return !loadable && filterable
        ? new Fuse(options, {
            keys: ['title', 'subtitle'],
            includeMatches: true,
            includeScore: true,
            threshold: exact ? 0.0 : 0.6,
          })
        : null;
    }),
  );

  private readonly matchIndices: Observable<
    [
      {
        [id: string]: FuseResultMatch[];
      },
      { [id: string]: number },
    ]
  > = combineLatest([
    this.fuzzySearch,
    this.debouncedFilter,
    this.defaultedOptionsLoadable,
  ]).pipe(
    map(([fuzzySearch, filter, loadable]) => {
      const matchedNodes: { [id: string]: FuseResultMatch[] } = {};
      const scores: { [id: string]: number } = {};
      if (!loadable) {
        if (filter.length > 0) {
          const matches = fuzzySearch!.search(filter);
          if (matches.length) {
            for (const match of matches) {
              matchedNodes[match.item.id] = [...(match.matches || [])];
              scores[match.item.id] = match.score || 1;
            }
          }
        }
      }
      return [matchedNodes, scores];
    }),
  );

  readonly nodes: Observable<Node[]> = combineLatest([
    this.defaultedOptions,
    this.defaultedOptionsLoadable,
    this.matchIndices,
    this.defaultedSelectedOptionIds,
    this.debouncedFilter,
  ]).pipe(
    map(([options, loadable, [matches, scores], selectedOptionIds, filter]) => {
      return buildNodes(
        options,
        loadable,
        matches,
        scores,
        selectedOptionIds,
        filter,
      );
    }),
    replayLast(),
  );

  readonly filterEmpty = combineLatest([this.nodes, this.filter]).pipe(
    map(([nodes, filter]) => filter.length > 0 && nodes.length === 0),
  );

  public readonly nodesLimited: Observable<Node[]> = this.nodes.pipe(
    withLatestFrom(this.defaultedFilterable),
    map(([nodes, filterable]) => {
      return filterable
        ? (nodes || []).slice(0, this.maxElements ?? 100)
        : nodes;
    }),
  );

  public readonly notShownCount: Observable<number> = combineLatest([
    this.nodes,
    this.nodesLimited,
    this.defaultedOptionsLoadable.pipe(
      filter(o => !!o),
      map(o => o!),
      success(),
      startWith(null),
    ),
  ]).pipe(
    map(([nodes, nodesLimited, loadedOptions]) =>
      Math.max(
        0,
        (get(loadedOptions, 'count', null) || nodes.length) -
          nodesLimited.length,
      ),
    ),
  );

  @Output('selectedOptionIdsChange')
  readonly selectedOptionIds = merge(
    this.select.pipe(
      filter(select => select[0] !== null),
      map(select => ({
        type: 'change',
        value: select,
      })),
    ),
    this._selectedOptionIds.pipe(map(ids => ({ type: 'init', value: ids }))),
  ).pipe(
    scan(
      (accum, event) => {
        switch (event.type) {
          case 'init':
            accum = [...(event.value || [])];
            break;
          case 'change': {
            const [id, selected] = event.value;
            accum = [...accum];
            if (selected) {
              if (accum.indexOf(id) === -1) {
                accum.push(id);
              }
            } else {
              if (accum.indexOf(id) >= 0) {
                accum.splice(accum.indexOf(id), 1);
              }
            }
          }
        }
        return accum;
      },
      <(string | boolean | null)[]>[],
    ),
    distinctUntilChanged((ids1, ids2) => {
      return _.isEqual(new Set(ids1), new Set(ids2));
    }),
  );

  readonly selectedOptionIdsChange: Observable<string[]> = this.select.pipe(
    filter(select => {
      const [id] = select;
      return id !== null;
    }),
    withLatestFrom(this.nodes),
    map(([select, nodes]) => {
      const [id, selected] = select;
      updateSelection(nodes, id!, selected);
      return nodesToSelectedIds(nodes);
    }),
  );

  trackBy(index: number, node: Node) {
    return node.id;
  }

  readonly selectedNodes: Observable<Node[]> = this.nodes.pipe(
    map(nodes => nodes.filter(node => !!node.selected)),
  );

  readonly resize = this.eventsService.resize.pipe(startWith(null));

  readonly windowSize = this.resize.pipe(
    map(() => ({ width: window.innerWidth, height: window.innerHeight })),
  );

  readonly anchorBounds: Observable<Box> = combineLatest([
    this._anchor,
    this.resize,
  ]).pipe(
    map(([anchor]) =>
      anchor instanceof Element ? anchor.getBoundingClientRect() : anchor,
    ),
    map(anchor => ({
      x: anchor.left,
      y: anchor.top,
      width: anchor.right - anchor.left,
      height: anchor.bottom - anchor.top,
    })),
  );

  readonly optionHeight: Observable<number> = combineLatest([
    this.windowSize,
    this.anchorBounds,
    this.defaultedFilterPositionBottom,
  ]).pipe(
    map(([{ height }, anchor, position]) => {
      if (position) {
        return Math.min(400, 400 - anchor.y);
      } else {
        return Math.min(400, height - anchor.y - anchor.height - 110);
      }
    }),
  );
}

function buildNodes(
  options: Option[],
  loadable: Loadable<{ count: number; options: Option[] }> | null,
  matches: { [id: string]: FuseResultMatch[] },
  scores: { [id: string]: number },
  selectedOptions: string[],
  filter: string,
): Node[] {
  const sort = !isEmpty(scores);
  const selectedIds = new Set(selectedOptions || []);
  let nodes = options
    ? options.map(option => {
        let titleHighlightIndices: HighlightIndices = [];
        let subtitleHighlightIndices: HighlightIndices = [];
        for (const fuseMatch of matches[option.id] || []) {
          switch (fuseMatch.key) {
            case 'title':
              titleHighlightIndices = fuseMatch.indices.concat();
              break;
            case 'subtitle':
              subtitleHighlightIndices = fuseMatch.indices.concat();
              break;
          }
        }
        const node: Node = {
          ...option,
          selected: selectedIds.has(option.id.toString()),
          titleHighlightIndices,
          subtitleHighlightIndices,
          score: scores[option.id],
        };
        return node;
      })
    : [];
  if (!loadable && filter.length > 0) {
    nodes = nodes.filter(
      n =>
        n.titleHighlightIndices.length > 0 ||
        n.subtitleHighlightIndices.length > 0,
    );
  }
  nodes = sort ? sortBy(nodes, node => node.score) : nodes;
  return nodes;
}

function nodesToSelectedIds(nodes: Node[]): string[] {
  const selectedOptions: string[] = [];
  for (const node of nodes) {
    if (node.selected) {
      selectedOptions.push(node.id.toString());
    }
  }
  return selectedOptions;
}

function updateSelection(nodes: Node[], id: string, selected: boolean): void {
  for (const node of nodes) {
    const match = id === node.id.toString();
    if (match) {
      node.selected = selected;
    }
  }
}
